(() => {
  const CART_URL = '/cart.js';
  const ADD_URL = '/cart/add.js';
  const CHANGE_URL = '/cart/change.js';
  const UPDATE_URL = '/cart/update.js';
  // Thresholds match the enforced checkout discount function (cart-threshold-discount).
  const SHIPPING_THRESHOLD = 6500;   // $65 -> free shipping
  const REWARD_10_THRESHOLD = 13000; // $130 -> 10% off
  const REWARD_15_THRESHOLD = 22000; // $220 -> 15% off

  const state = {
    cart: null,
    open: false,
    loading: false,
  };

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const formatMoney = (cents, currency = 'USD') => {
    const locale = document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  };

  const subscriptionCadence = (item) => {
    if (!item?.selling_plan_allocation) return '';
    const planName = String(item.selling_plan_allocation.selling_plan?.name || '').toLowerCase();
    if (planName.includes('year') || planName.includes('annual')) return 'Every year';
    if (planName.includes('week')) return 'Every week';
    return 'Every 30 days';
  };

  const bundleInstanceId = (item) => item?.properties?._zb_bundle_instance_id || '';
  const bundleRequiredCount = (item) => Number(item?.properties?._zb_bundle_required_count || 0);
  const bundleTargetCents = (item) => Number(item?.properties?._zb_bundle_target_cents || 0);
  const isBundleLine = (item) => Boolean(bundleInstanceId(item));

  const bundleGroups = (cart = state.cart) => {
    const groups = new Map();
    for (const item of cart?.items || []) {
      const instanceId = bundleInstanceId(item);
      if (!instanceId) continue;
      const existing = groups.get(instanceId) || { items: [], required: bundleRequiredCount(item), targetCents: 0 };
      existing.items.push(item);
      existing.required = Math.max(existing.required, bundleRequiredCount(item));
      existing.targetCents = Math.max(existing.targetCents, bundleTargetCents(item));
      groups.set(instanceId, existing);
    }
    return groups;
  };

  const incompleteBundleGroups = (cart = state.cart) =>
    Array.from(bundleGroups(cart).entries()).filter(([, group]) => group.required && group.items.length < group.required);

  const hasIncompleteBundles = (cart = state.cart) => incompleteBundleGroups(cart).length > 0;

  // Total cents the bundle-discount checkout function removes from the cart:
  // for each complete bundle group, (sum of component prices - bundle target).
  const computeBundleDiscountCents = (cart = state.cart) => {
    let cents = 0;
    for (const [, group] of bundleGroups(cart)) {
      if (!group.required || group.items.length !== group.required || !group.targetCents) continue;
      const groupRawCents = group.items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
      if (groupRawCents <= group.targetCents) continue;
      cents += groupRawCents - group.targetCents;
    }
    return cents;
  };

  // Cart total AFTER bundle discounts — the basis for every cart reward, matching
  // the enforced checkout functions. items_subtotal_price counts bundle items at
  // full price (the bundle discount is an order-level function discount applied
  // at checkout), so subtract it here.
  const effectiveSubtotal = (cart = state.cart) => {
    const raw = cart?.items_subtotal_price || cart?.total_price || 0;
    return Math.max(0, raw - computeBundleDiscountCents(cart));
  };

  // Mirror the enforced checkout function: tier unlocks on the post-bundle
  // cart total; the % is applied to non-bundle lines only.
  const rewardTier = (cart) => {
    let nonBundleCents = 0;
    for (const item of cart?.items || []) {
      if (isBundleLine(item)) continue;
      nonBundleCents += item.final_line_price || 0;
    }
    const basis = effectiveSubtotal(cart);
    let pct = 0;
    if (basis >= REWARD_15_THRESHOLD) pct = 15;
    else if (basis >= REWARD_10_THRESHOLD) pct = 10;
    return { pct, discountCents: Math.round((nonBundleCents * pct) / 100) };
  };

  const cartPricing = (cart = state.cart) => {
    const rawTotal = cart?.total_price || 0;
    const bundleDiscountCents = computeBundleDiscountCents(cart);
    const reward = rewardTier(cart);

    return {
      rawTotal,
      // cart.total_price ALREADY reflects the automatic discounts (bundle + cart reward),
      // exactly like the checkout page — so do not subtract them again here.
      displayTotal: rawTotal,
      itemsSubtotal: cart?.items_subtotal_price || rawTotal,
      totalDiscount: cart?.total_discount || 0,
      bundleDiscountCents,
      rewardPct: reward.pct,
      rewardDiscountCents: reward.discountCents,
    };
  };

  const fetchCart = async () => {
    const response = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart fetch failed');
    state.cart = await response.json();
    await enforceSingleSubscription();
    render();
    return state.cart;
  };

  const postCart = async (url, payload) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    state.cart = data.items ? data : await fetchCart();
    render();
    return state.cart;
  };

  const getDrawer = () => qs('[data-zb-ajax-cart]');

  const setOpen = (open) => {
    const drawer = getDrawer();
    if (!drawer) return;
    state.open = open;
    drawer.hidden = !open;
    drawer.dataset.open = open ? 'true' : 'false';
    document.documentElement.classList.toggle('zb-cart-drawer-open', open);
    qsa('[data-zb-cart-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
    if (open && !state.cart) fetchCart().catch(console.error);
  };

  const rewardBasis = (cart) => effectiveSubtotal(cart);

  const rewardMessage = (cart) => {
    const s = rewardBasis(cart);
    const cur = cart.currency;
    if (s < SHIPPING_THRESHOLD) {
      return `${formatMoney(SHIPPING_THRESHOLD - s, cur)} away from free shipping`;
    }
    if (s < REWARD_10_THRESHOLD) {
      return `Free shipping unlocked · ${formatMoney(REWARD_10_THRESHOLD - s, cur)} away from 10% off`;
    }
    if (s < REWARD_15_THRESHOLD) {
      return `Free shipping + 10% off · ${formatMoney(REWARD_15_THRESHOLD - s, cur)} away from 15% off`;
    }
    return 'Free shipping + 15% off unlocked';
  };

  const renderRewards = (cart) => {
    const s = rewardBasis(cart);
    const progress = Math.min(100, Math.round((s / REWARD_15_THRESHOLD) * 100));
    const ship = s >= SHIPPING_THRESHOLD;
    const d10 = s >= REWARD_10_THRESHOLD;
    const d15 = s >= REWARD_15_THRESHOLD;

    return `
      <div class="zb-cart-progress">
        <div class="zb-cart-progress__text">${escapeHtml(rewardMessage(cart))}</div>
        <div class="zb-cart-progress__track" aria-hidden="true"><span style="width:${progress}%"></span></div>
        <div class="zb-cart-progress__milestones">
          <span class="${ship ? 'is-unlocked' : ''}">Free ship $65 (USA)</span>
          <span class="${d10 ? 'is-unlocked' : ''}">10% $130</span>
          <span class="${d15 ? 'is-unlocked' : ''}">15% $220</span>
        </div>
      </div>
    `;
  };

  let lastRecProductId = null;
  // Cached `/products/{handle}.js` lookups. Used for the empty-cart seed product
  // and to force the book into the suggestions (the recommendation graph on a
  // small catalog often won't link it to the ION products on its own).
  const productHandleCache = new Map();
  const fetchProductByHandle = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!productHandleCache.has(handle)) {
      productHandleCache.set(handle, fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null));
    }
    return productHandleCache.get(handle);
  };
  const renderRecommendations = async () => {
    const drawer = getDrawer();
    const container = drawer && qs('[data-zb-cart-recs]', drawer);
    if (!container) return;
    const cart = state.cart;
    const firstItem = (cart?.items || []).find((it) => !isBundleLine(it)) || (cart?.items || [])[0];
    const seedHandle = container.getAttribute('data-zb-recs-seed-handle') || 'ion-gut-support';
    const bookHandle = container.getAttribute('data-zb-recs-book-handle') || '';
    // Seed off the first cart item, or fall back to the flagship product so the
    // suggestions still render for an empty cart.
    const pid = firstItem ? firstItem.product_id : (await fetchProductByHandle(seedHandle))?.id;
    if (!pid) { container.hidden = true; container.innerHTML = ''; lastRecProductId = null; return; }
    if (pid === lastRecProductId && container.innerHTML) return; // already loaded for this product
    lastRecProductId = pid;
    try {
      const res = await fetch(`/recommendations/products.json?product_id=${pid}&limit=6&intent=related`, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      const inCart = new Set((cart.items || []).map((it) => it.product_id));
      // Only suggest ION products + the book — never the education / journey options.
      const isExcluded = (p) => /journey|education|subscription|membership|discovery|embodiment|empowerment|enroll/i.test(`${p.handle || ''} ${p.product_type || ''} ${p.title || ''}`);
      const usable = (p) => Boolean(p && p.variants && p.variants[0] && !inCart.has(p.id) && !isExcluded(p));
      let recs = (data.products || []).filter(usable);
      // Always surface the book first, even when it's absent from the related set.
      const book = await fetchProductByHandle(bookHandle);
      if (usable(book) && !recs.some((p) => p.id === book.id)) recs.unshift(book);
      recs = recs.slice(0, 3);
      if (!recs.length) { container.hidden = true; container.innerHTML = ''; return; }
      const currency = cart.currency || 'USD';
      container.hidden = false;
      container.innerHTML = `
        <div class="zb-cart-recs__title">You may also like</div>
        <div class="zb-cart-recs__grid">
          ${recs.map((p) => {
            const v = p.variants.find((x) => x.available) || p.variants[0];
            const img = p.featured_image ? `<img src="${p.featured_image}&width=200" alt="" loading="lazy">` : '';
            return `<div class="zb-cart-recs__card">
              <a class="zb-cart-recs__media" href="${p.url}">${img}</a>
              <div class="zb-cart-recs__info">
                <a class="zb-cart-recs__name" href="${p.url}">${escapeHtml(p.title)}</a>
                <span class="zb-cart-recs__price">${formatMoney(v.price, currency)}</span>
              </div>
              <button class="zb-cart-recs__add" type="button" data-zb-rec-add="${v.id}"${v.available ? '' : ' disabled'}>Add</button>
            </div>`;
          }).join('')}
        </div>`;
    } catch (error) {
      container.hidden = true;
    }
  };

  const renderItem = (item, currency) => {
    const bundled = isBundleLine(item);
    const image = item.image
      ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy">`
      : '<span></span>';
    const variant = item.variant_title && item.variant_title !== 'Default Title'
      ? `<small>${escapeHtml(item.variant_title)}</small>`
      : '';
    const cadence = subscriptionCadence(item)
      ? `<small class="zb-ajax-cart__subscription">${escapeHtml(subscriptionCadence(item))}</small>`
      : '';
    const bundleNote = bundled
      ? '<small class="zb-ajax-cart__bundle">Bundle item</small>'
      : '';
    const quantityControls = bundled
      ? '<div class="zb-ajax-cart__bundle-lock">Quantity fixed in bundle</div>'
      : `
          <div class="zb-ajax-cart__qty" aria-label="Quantity">
            <button type="button" data-zb-cart-qty="-1" data-key="${escapeHtml(item.key)}" aria-label="Decrease quantity">-</button>
            <input type="number" min="0" value="${item.quantity}" data-zb-cart-qty-input data-key="${escapeHtml(item.key)}" aria-label="Quantity for ${escapeHtml(item.product_title)}">
            <button type="button" data-zb-cart-qty="1" data-key="${escapeHtml(item.key)}" aria-label="Increase quantity">+</button>
          </div>
        `;

    return `
      <article class="zb-ajax-cart__item" data-zb-cart-line="${escapeHtml(item.key)}" data-zb-bundle-instance="${escapeHtml(bundleInstanceId(item))}">
        <a class="zb-ajax-cart__media" href="${escapeHtml(item.url)}">${image}</a>
        <div class="zb-ajax-cart__meta">
          <a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
          ${variant}
          ${cadence}
          ${bundleNote}
          ${quantityControls}
          <button class="zb-ajax-cart__remove" type="button" data-zb-cart-remove data-key="${escapeHtml(item.key)}">${bundled ? 'Remove bundle' : 'Remove'}</button>
        </div>
        <strong>${formatMoney(item.final_line_price, currency)}</strong>
      </article>
    `;
  };

  const render = () => {
    const drawer = getDrawer();
    const cart = state.cart;
    if (!drawer || !cart) return;

    const count = cart.item_count || 0;
    const countText = `${count} item${count === 1 ? '' : 's'}`;
    const currency = cart.currency || 'USD';
    const pricing = cartPricing(cart);
    // The cart-threshold function enforces the discount automatically at checkout,
    // so no discount code is appended (that would double-discount).
    const checkoutHref = '/checkout';
    const incomplete = hasIncompleteBundles(cart);

    qsa('[data-zb-cart-trigger]').forEach((trigger) => {
      trigger.hidden = count === 0;
      trigger.setAttribute('aria-label', `Cart, ${countText}`);
    });
    qsa('[data-zb-cart-count]').forEach((node) => {
      node.textContent = String(count);
    });

    const title = qs('[data-zb-cart-title]', drawer);
    const rewards = qs('[data-zb-cart-rewards]', drawer);
    const items = qs('[data-zb-cart-items]', drawer);
    const subtotal = qs('[data-zb-cart-subtotal]', drawer);
    const savings = qs('[data-zb-cart-bundle-savings]', drawer);
    const checkout = qs('[data-zb-cart-checkout]', drawer);

    if (title) title.textContent = countText;
    if (rewards) rewards.innerHTML = renderRewards(cart);
    if (subtotal) subtotal.textContent = formatMoney(pricing.displayTotal, currency);
    if (savings) {
      const rows = [];
      if (pricing.bundleDiscountCents > 0) rows.push(`<span>Bundle savings</span><strong>-${formatMoney(pricing.bundleDiscountCents, currency)}</strong>`);
      if (pricing.rewardDiscountCents > 0) rows.push(`<span>Cart reward (${pricing.rewardPct}% off)</span><strong>-${formatMoney(pricing.rewardDiscountCents, currency)}</strong>`);
      savings.hidden = rows.length === 0;
      savings.innerHTML = rows.map((r) => `<div class="zb-ajax-cart__savings-row">${r}</div>`).join('');
    }
    if (checkout) {
      checkout.href = incomplete ? '#' : checkoutHref;
      checkout.toggleAttribute('aria-disabled', incomplete);
      checkout.dataset.zbBundleBlocked = incomplete ? 'true' : 'false';
    }

    if (items) {
      items.innerHTML = count
        ? `${incomplete ? '<div class="zb-ajax-cart__warning">This bundle is incomplete. Remove the bundle or add it again before checkout.</div>' : ''}${cart.items.map((item) => renderItem(item, currency)).join('')}`
        : '<div class="zb-ajax-cart__empty"><strong>Your cart is empty.</strong><span>Add a product to unlock free shipping at $65.</span></div>';
    }

    renderRecommendations();
  };

  const setLoading = (element, loading) => {
    state.loading = loading;
    if (!element) return;
    element.toggleAttribute('aria-busy', loading);
    element.disabled = loading;
    if (loading) {
      element.dataset.originalText = element.textContent;
      element.textContent = 'Adding...';
    } else if (element.dataset.originalText) {
      element.textContent = element.dataset.originalText;
      delete element.dataset.originalText;
    }
  };

  const addForm = async (form, submitter) => {
    setLoading(submitter, true);
    try {
      const formData = new FormData(form);
      const response = await fetch(ADD_URL, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');
      await fetchCart();
      setOpen(true);
    } catch (error) {
      console.error(error);
      window.alert(error.message || 'Could not add this item to cart.');
    } finally {
      setLoading(submitter, false);
    }
  };

  const addItems = async (items, submitter) => {
    setLoading(submitter, true);
    try {
      await postCart(ADD_URL, { items });
      await fetchCart();
      setOpen(true);
    } catch (error) {
      console.error(error);
      window.alert(error.message || 'Could not add this bundle to cart.');
    } finally {
      setLoading(submitter, false);
    }
  };

  const changeLine = async (key, quantity) => {
    const drawer = getDrawer();
    drawer?.classList.add('is-loading');
    try {
      const item = state.cart?.items?.find((line) => line.key === key);
      if (item && isBundleLine(item)) {
        if (Number(quantity) <= 0) {
          await removeBundleInstance(bundleInstanceId(item));
        } else {
          window.alert('Bundle quantities are fixed. Remove the bundle and add it again to change it.');
        }
        return;
      }
      // Subscriptions (e.g. Journey) are limited to a single quantity.
      if (item && item.selling_plan_allocation && Number(quantity) > 1) {
        window.alert('Only one Journey subscription can be in your cart.');
        await postCart(CHANGE_URL, { id: key, quantity: 1 });
        return;
      }
      await postCart(CHANGE_URL, { id: key, quantity: Math.max(0, Number(quantity) || 0) });
    } catch (error) {
      console.error(error);
      window.alert(error.message || 'Could not update cart.');
    } finally {
      drawer?.classList.remove('is-loading');
    }
  };

  const removeBundleInstance = async (instanceId) => {
    if (!instanceId) return;
    const updates = {};
    for (const item of state.cart?.items || []) {
      if (bundleInstanceId(item) === instanceId) updates[item.key] = 0;
    }
    if (!Object.keys(updates).length) return;
    await postCart(UPDATE_URL, { updates });
  };

  const sanitizeIncompleteBundles = async () => {
    const incomplete = incompleteBundleGroups();
    if (!incomplete.length) return false;
    const updates = {};
    for (const [, group] of incomplete) {
      for (const item of group.items) updates[item.key] = 0;
    }
    if (!Object.keys(updates).length) return false;
    await postCart(UPDATE_URL, { updates });
    return true;
  };

  // Enforce one standalone subscription per product (e.g. Journey): only a
  // single line, quantity 1. Different selling plans of the same product
  // (monthly vs annual) collapse to the most recently added one, so adding
  // annual over monthly replaces it. Bundle-component subscription lines are
  // left untouched. Uses a raw fetch (not postCart) to avoid re-entrancy with
  // fetchCart. Returns true if it corrected the cart.
  let enforcingSubscription = false;
  const enforceSingleSubscription = async () => {
    if (enforcingSubscription) return false;
    const cart = state.cart;
    if (!cart || !Array.isArray(cart.items)) return false;

    const byProduct = new Map();
    for (const item of cart.items) {
      if (isBundleLine(item)) continue;
      if (!item.selling_plan_allocation) continue; // subscription lines only
      const list = byProduct.get(item.product_id) || [];
      list.push(item);
      byProduct.set(item.product_id, list);
    }

    const updates = {};
    for (const [, lines] of byProduct) {
      const keep = lines[lines.length - 1]; // most recently added
      for (const line of lines) {
        if (line === keep) {
          if (line.quantity !== 1) updates[line.key] = 1;
        } else {
          updates[line.key] = 0;
        }
      }
    }
    if (!Object.keys(updates).length) return false;

    enforcingSubscription = true;
    try {
      const res = await fetch(UPDATE_URL, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (data && data.items) state.cart = data;
    } catch (error) {
      console.error(error);
    } finally {
      enforcingSubscription = false;
    }
    return true;
  };

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const recAdd = target.closest('[data-zb-rec-add]');
    if (recAdd) {
      event.preventDefault();
      const id = Number(recAdd.getAttribute('data-zb-rec-add'));
      if (id) {
        recAdd.disabled = true;
        recAdd.textContent = 'Adding…';
        addItems([{ id, quantity: 1 }]).catch(() => { recAdd.disabled = false; recAdd.textContent = 'Add'; });
      }
      return;
    }

    const open = target.closest('[data-zb-cart-trigger]');
    if (open) {
      event.preventDefault();
      setOpen(true);
      return;
    }

    if (target.closest('[data-zb-cart-close]')) {
      event.preventDefault();
      setOpen(false);
      return;
    }

    const qty = target.closest('[data-zb-cart-qty]');
    if (qty) {
      const key = qty.getAttribute('data-key');
      const delta = Number(qty.getAttribute('data-zb-cart-qty'));
      const input = key ? qs(`[data-zb-cart-qty-input][data-key="${CSS.escape(key)}"]`) : null;
      if (key && input instanceof HTMLInputElement) {
        changeLine(key, Number(input.value) + delta);
      }
      return;
    }

    const remove = target.closest('[data-zb-cart-remove]');
    if (remove) {
      const key = remove.getAttribute('data-key');
      if (key) changeLine(key, 0);
      return;
    }

    const checkout = target.closest('[data-zb-cart-checkout], [name="checkout"], a[href="/checkout"], a[href^="/discount/"]');
    if (checkout) {
      if (checkout.dataset.zbBundleBlocked === 'true' || hasIncompleteBundles()) {
        event.preventDefault();
        sanitizeIncompleteBundles()
          .then((removed) => {
            window.alert(removed ? 'That bundle was incomplete, so it was removed from your cart. Add the bundle again before checkout.' : 'This bundle is incomplete. Add the full bundle before checkout.');
          })
          .catch((error) => {
            console.error(error);
            window.alert('Could not validate this bundle. Please refresh the cart before checkout.');
          });
      }
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-zb-cart-qty-input]')) return;
    const key = input.getAttribute('data-key');
    if (key) changeLine(key, Number(input.value));
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('#cart-form') && hasIncompleteBundles()) {
      event.preventDefault();
      sanitizeIncompleteBundles()
        .then(() => window.alert('That bundle was incomplete, so it was removed from your cart. Add the bundle again before checkout.'))
        .catch((error) => {
          console.error(error);
          window.alert('Could not validate this bundle. Please refresh the cart before checkout.');
        });
      return;
    }
    if (!form.matches('[data-zb-ajax-product-form]')) return;
    event.preventDefault();
    addForm(form, event.submitter instanceof HTMLButtonElement ? event.submitter : qs('[type="submit"]', form));
  });

  document.addEventListener('cart:update', () => {
    fetchCart().catch(console.error);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });

  window.ZachCart = {
    addItems,
    open: () => setOpen(true),
    close: () => setOpen(false),
    refresh: fetchCart,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fetchCart, { once: true });
  } else {
    fetchCart().catch(console.error);
  }
})();
